import React from 'react';
import { Link } from 'react-router-dom';
import ScoreGauge from './ScoreGauge';
import PageSEO from './PageSEO';

const riskColors = { low: '#10b981', medium: '#f59e0b', high: '#ef4444' };

function SampleReport() {
  // Realistic sample data — mirrors what a real TU-backed screening report produces
  const screening = {
    tenant_name: 'Sarah Connor',
    tenant_email: 'sarah.connor@email.com',
    rental_address: '85 Queen St W, Toronto, ON',
    rental_unit: '1204',
    rent_amount: 2200,
    report_generated_at: new Date().toISOString(),
  };

  const td = {
    full_name: 'Sarah Anne Connor',
    employer: 'Shopify Inc.',
    job_title: 'Product Designer',
    monthly_income: 6800,
    years_employed: '3 years',
    previous_landlord_name: 'Kyle Reese',
    previous_landlord_phone: '(416) 555-0187',
  };

  const summary = {
    totalTradelines: 14,
    openTradelines: 9,
    closedTradelines: 5,
    revolvingTrades: 6,
    installmentTrades: 2,
    mortgageTrades: 1,
    utilizationPct: 22,
    totalAvailableCredit: 28400,
    totalDebt: 8200,
    totalMonthlyDebt: 680,
    oldestAccountYears: 7,
    totalLatePayments: 1,
    negativeTrades: 0,
    historicalNegative: 1,
    inquiries12m: 2,
  };

  const scoreReasons = ['BC', 'AT', 'S0'];

  const ai = {
    risk_level: 'low',
    summary: 'Sarah Connor presents a strong rental applicant profile. Her TransUnion credit score of 742 is well above the national median, supported by 14 trade lines with a 7-year credit history and only one historical late payment. Her credit utilization sits at a healthy 22% with $28,400 in available credit. Self-reported monthly income of $6,800 at Shopify Inc. (3 years tenure) places the rent-to-income ratio at 32.4%, within the recommended 35% threshold.',
    credit_highlights: [
      'Credit score 742 — well above national median (680)',
      '14 trade lines with 7-year history — strong credit depth',
      'Credit utilization 22% — well within healthy range',
      'Only 1 historical late payment across all accounts',
      'No collections, bankruptcies, judgments, or liens',
      'Mortgage trade line on file — indicates prior lending approval',
      'Total monthly debt obligations $680 — manageable alongside rent',
    ],
    concerns: [],
    income_note: 'Income of $6,800/month is self-reported and has not been independently verified. Bureau employment records confirm Shopify Inc. as employer, which is consistent with the applicant\'s disclosure. Rent-to-income ratio of 32.4% and debt-to-income of 10.0% are both within acceptable ranges. Consider requesting a recent pay stub for additional verification.',
    employment_match: 'match',
    credit_utilization_rating: 'low',
    payment_history_rating: 'excellent',
    recommendation: 'Low-risk applicant with strong credit history, stable employment, and comfortable income coverage. Previous landlord reference available for follow-up. Recommend proceeding with standard lease terms.',
  };

  const riskLevel = 'low';
  const riskColor = riskColors[riskLevel];
  const rtiPct = Math.round((screening.rent_amount / td.monthly_income) * 100);

  const sectionStyle = {
    padding: '1.25rem 1.5rem', borderRadius: '12px',
    border: '1px solid var(--border-color, #e5e7eb)', marginBottom: '1rem'
  };
  const labelStyle = { fontSize: '0.78rem', color: 'var(--light-text-color)', fontWeight: 500, marginBottom: '0.15rem' };
  const valueStyle = { fontSize: '0.95rem', fontWeight: 600 };

  return (
    <main className="App-main">
      <PageSEO title="Sample Tenant Screening Report | See What You Get | Rentual" description="See a real example of Rentual's tenant screening report with credit score, risk assessment, AI summary, and detailed credit profile." path="/sample-report" />
      <div style={{ maxWidth: 680, margin: '2rem auto' }}>

        {/* Sample banner */}
        <div style={{
          background: 'rgba(40,182,200,0.06)',
          border: '1px dashed var(--accent-color, #28b6c8)',
          borderRadius: '10px', padding: '0.75rem 1rem', marginBottom: '1.5rem', textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--accent-color, #28b6c8)', fontWeight: 600 }}>
            📋 Sample Report — This is what your screening report looks like. All data is fictional.
          </p>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ margin: '0 0 0.25rem' }}>Screening Report</h2>
          <p className="status-text">
            {td.full_name} · {screening.rental_address}, Unit {screening.rental_unit}
          </p>
          <p className="status-text" style={{ fontSize: '0.78rem' }}>
            Generated {new Date(screening.report_generated_at).toLocaleDateString()}
          </p>
        </div>

        {/* ── KPI Gauges ── */}
        <div style={{
          ...sectionStyle, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          gap: '1.5rem', flexWrap: 'wrap', padding: '1.5rem'
        }}>
          <ScoreGauge value={742} minValue={300} maxValue={900} label="Credit Score" sublabel="CreditVision" rating="Good" ratingColor="#16A34A" size={130} />
          <ScoreGauge value={rtiPct} minValue={0} maxValue={100} label="Rent-to-Income" sublabel={`$${screening.rent_amount.toLocaleString()} / $${td.monthly_income.toLocaleString()}`} rating="Healthy" ratingColor="#16A34A" size={130} invertColors={true} />
          <ScoreGauge value={summary.utilizationPct} minValue={0} maxValue={100} label="Credit Utilization" rating="Low" ratingColor="#16A34A" size={130} invertColors={true} />
          <ScoreGauge value={20} minValue={0} maxValue={100} label="Risk Level" rating="Low" ratingColor={riskColor} size={130} invertColors={true} />
        </div>

        {/* ── AI Summary ── */}
        <div style={sectionStyle}>
          <p style={{ ...labelStyle, marginBottom: '0.5rem' }}>AI Assessment</p>
          <p style={{ fontSize: '0.92rem', lineHeight: 1.6, margin: 0 }}>{ai.summary}</p>
          <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.85rem', borderRadius: '8px', background: 'rgba(40,182,200,0.06)', border: '1px solid rgba(40,182,200,0.15)' }}>
            <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.5 }}>
              <strong style={{ color: 'var(--accent-color)' }}>Recommendation:</strong> {ai.recommendation}
            </p>
          </div>
          <p className="status-text" style={{ marginTop: '0.75rem', fontSize: '0.82rem', fontStyle: 'italic' }}>
            {ai.income_note}
          </p>
        </div>

        {/* ── Credit Profile ── */}
        <div style={sectionStyle}>
          <p style={{ ...labelStyle, marginBottom: '0.75rem' }}>Credit Profile (TransUnion)</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem' }}>
            <div>
              <p style={labelStyle}>Tradelines</p>
              <p style={valueStyle}>{summary.totalTradelines} <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--light-text-color)' }}>({summary.openTradelines} open)</span></p>
            </div>
            <div>
              <p style={labelStyle}>Collections</p>
              <p style={valueStyle}>None</p>
            </div>
            <div>
              <p style={labelStyle}>Bankruptcies</p>
              <p style={valueStyle}>None</p>
            </div>
            <div>
              <p style={labelStyle}>Judgments / Liens</p>
              <p style={valueStyle}>None</p>
            </div>
            <div>
              <p style={labelStyle}>Late Payments</p>
              <p style={{ ...valueStyle, color: '#f59e0b' }}>{summary.totalLatePayments}</p>
            </div>
            <div>
              <p style={labelStyle}>Account Age</p>
              <p style={valueStyle}>{summary.oldestAccountYears} yr</p>
            </div>
            <div>
              <p style={labelStyle}>Total Debt</p>
              <p style={valueStyle}>${summary.totalDebt.toLocaleString()}</p>
            </div>
            <div>
              <p style={labelStyle}>Monthly Debt</p>
              <p style={valueStyle}>${summary.totalMonthlyDebt.toLocaleString()}</p>
            </div>
            <div>
              <p style={labelStyle}>Recent Inquiries</p>
              <p style={valueStyle}>{summary.inquiries12m}</p>
            </div>
          </div>

          {/* Score reason codes */}
          <div style={{ marginTop: '1rem' }}>
            <p style={labelStyle}>Score Factor Codes</p>
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
              {scoreReasons.map((code, i) => (
                <span key={i} style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: '999px', background: 'var(--secondary-color, #f4f6fb)', border: '1px solid var(--border-color)' }}>
                  {code}
                </span>
              ))}
            </div>
          </div>

          {/* Key findings */}
          <div style={{ marginTop: '1rem' }}>
            <p style={labelStyle}>Key Findings</p>
            <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.25rem' }}>
              {ai.credit_highlights.map((h, i) => (
                <li key={i} style={{ fontSize: '0.88rem', lineHeight: 1.5 }}>{h}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* ── Income & Employment ── */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <p style={{ ...labelStyle, marginBottom: 0 }}>Income & Employment</p>
            <span style={{ fontSize: '0.7rem', background: '#f59e0b20', color: '#f59e0b', padding: '0.15rem 0.4rem', borderRadius: '999px', fontWeight: 600 }}>Self-reported</span>
            <span style={{ fontSize: '0.7rem', fontWeight: 600, padding: '0.15rem 0.4rem', borderRadius: '999px', background: '#10b98120', color: '#10b981' }}>✓ Bureau match</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <p style={labelStyle}>Employer</p>
              <p style={valueStyle}>{td.employer}</p>
            </div>
            <div>
              <p style={labelStyle}>Job Title</p>
              <p style={valueStyle}>{td.job_title}</p>
            </div>
            <div>
              <p style={labelStyle}>Monthly Income</p>
              <p style={valueStyle}>${Number(td.monthly_income).toLocaleString()}</p>
            </div>
            <div>
              <p style={labelStyle}>Years Employed</p>
              <p style={valueStyle}>{td.years_employed}</p>
            </div>
          </div>
        </div>

        {/* ── AI Ratings ── */}
        <div style={sectionStyle}>
          <p style={{ ...labelStyle, marginBottom: '0.75rem' }}>AI Ratings</p>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 200px', padding: '0.75rem', borderRadius: '10px', background: 'var(--secondary-color, #f4f6fb)', textAlign: 'center' }}>
              <p style={labelStyle}>Payment History</p>
              <p style={{ fontSize: '1rem', fontWeight: 700, margin: '0.25rem 0 0', textTransform: 'capitalize', color: '#16A34A' }}>
                Excellent
              </p>
            </div>
            <div style={{ flex: '1 1 200px', padding: '0.75rem', borderRadius: '10px', background: 'var(--secondary-color, #f4f6fb)', textAlign: 'center' }}>
              <p style={labelStyle}>Credit Utilization</p>
              <p style={{ fontSize: '1rem', fontWeight: 700, margin: '0.25rem 0 0', textTransform: 'capitalize', color: '#16A34A' }}>
                Low
              </p>
            </div>
          </div>
        </div>

        {/* ── Previous Landlord ── */}
        <div style={sectionStyle}>
          <p style={{ ...labelStyle, marginBottom: '0.5rem' }}>Previous Landlord Reference</p>
          <p style={{ fontSize: '0.9rem', margin: '0 0 0.15rem' }}>{td.previous_landlord_name}</p>
          <p className="status-text" style={{ fontSize: '0.85rem', margin: 0 }}>{td.previous_landlord_phone}</p>
        </div>

        {/* ── Upsell ── */}
        <div style={{
          ...sectionStyle, textAlign: 'center', marginTop: '2rem',
          background: 'var(--surface-color, var(--secondary-color, #f9fafb))'
        }}>
          <p style={{ fontWeight: 700, fontSize: '1rem', margin: '0 0 0.35rem' }}>Want more confidence?</p>
          <p className="status-text" style={{ fontSize: '0.85rem', lineHeight: 1.55, marginBottom: '1rem' }}>
            With Pro, tenants upload paystubs and documents. Get verified income, fraud detection, and auto-generated leases.
          </p>
          <Link to="/pricing" className="secondary-button" style={{ textDecoration: 'none', display: 'inline-block', fontSize: '0.85rem' }}>
            Explore Pro — $20/mo
          </Link>
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem', marginBottom: '2rem' }}>
          <Link to="/register?intent=screen" className="primary-button" style={{ textDecoration: 'none', display: 'inline-block', fontSize: '0.95rem', padding: '0.75rem 2rem' }}>
            Screen your first tenant — $29 →
          </Link>
          <p className="status-text" style={{ marginTop: '0.75rem', fontSize: '0.8rem' }}>
            Landlord or tenant pays · Results in minutes · Identity verified
          </p>
        </div>
      </div>
    </main>
  );
}

export default SampleReport;
